import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowClickedEvent } from '@ag-grid-enterprise/all-modules'
import { BaseCrud } from '@/shared/models/cruds.models'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { ConfigurationLocalAccountingCustomersGridSidepanelComboElements, ConfigurationLocalAccountingCustomersGridState, initialConfigurationLocalAccountingCustomersGridState } from '@/modules/configurationModule/detail/local-accounting/billable/clients/components/clients-grid/configuration-local-accounting-billable-clients-grid.model'
import { BillableRateBillableConceptResponse } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/billable-rate-billable-concept/billable-rate-billable-concept.dto'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { StrictNullable } from '@/shared/types/app.types'
import { BModal } from 'bootstrap-vue'
import { ConfigurationCustomerPatchInBatchRequest, ConfigurationCustomerResponse } from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-services/configuration-local-accounting-billable-clients-customer-services.dto'
import ConfigurationLocalAccountingBillableClientsModal from '@/modules/configurationModule/detail/local-accounting/billable/clients/components/clients-modal/configuration-local-accounting-billable-clients-modal.vue'
import ConfigurationListSidepanelComp from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel'
import { ConfigurationListSidePanelEntity } from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel.model'
import ConfigurationListSidepanel from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel.vue'
import { CommonParams } from '@/api/resources/common/shared/models/common.models'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { configurationLocalAccountingBillableCustomerRatesService } from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-billable-rates-services/configuration-local-accounting-billable-clients-rates.service'
import { ConfigurationLocalAccountingBillableCustomerBillableRateResponse } from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-billable-rates-services/configuration-local-accounting-billable-clients-rates.dto'
import { configurationLocalAccountingBillableCustomerRuleSetService } from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/automatic-customer-rule-set-services/automatic-customer-rule-set-services.service'
import { ConfigurationAutomaticCustomeRuleSetResponse } from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/automatic-customer-rule-set-services/automatic-customer-rule-set-services.dto'
import { GridUtils } from '@/shared/util/grid-utils'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        GridDisplayOptions,
        ConfigurationLocalAccountingBillableClientsModal,
        ConfigurationListSidepanel
    }
})
export default class ConfigurationLocalAccountingBillableClientsGrid extends BaseCrudServerSideComponent<any> implements BaseCrud {
    @Ref() readonly customerModalWrapper!: typeof BModal
    @Ref() readonly configurationListSidepanel!: ConfigurationListSidepanelComp

    state: ConfigurationLocalAccountingCustomersGridState

    constructor() {
        super()
        this.state = initialConfigurationLocalAccountingCustomersGridState()
    }

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                this.configurationListSidepanel.hide()
                if (await this._deletelocalAccountingBillableRates(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///
    onUpdate(localAccountingBillableRate: BillableRateBillableConceptResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        this.configurationListSidepanel.hide()
        if (localAccountingBillableRate) {
            this.showModal(this.$tc('configuration.actions.update'), localAccountingBillableRate, this.customerModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(localAccountingBillableRate => localAccountingBillableRate.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.configurationListSidepanel.hide()
        this.showModal(this.$tc('configuration.actions.add'), null, this.customerModalWrapper)
    }

    async onActivate(): Promise<void> {
        await this._activateDeactivateSelection(true, this.state.selectedRows)
    }

    async onDeactivate(): Promise<void> {
        await this._activateDeactivateSelection(false, this.state.selectedRows)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: !this.canUpdate ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: !this.canDelete ? ['not-show'] : [''],
                action: this.onDelete
            },
            {
                name: params?.node?.data?.active ? this.$tc('configuration.actions.deactivate') : this.$tc('configuration.actions.activate'),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this._activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$tc('configuration.actions.activate'),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: this.onActivate
            },
            {
                name: this.$tc('configuration.actions.deactivate'),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: this.onDeactivate
            }
        ]
    }

    onRowClicked(event: RowClickedEvent): void {
        const customerSelected: ConfigurationCustomerResponse = event.data
        this.configurationListSidepanel.show({
            title: customerSelected.name,
            entities: [
                {
                    id: ConfigurationLocalAccountingCustomersGridSidepanelComboElements.RULES,
                    entityKey: 'configuration.modal.client.rule'
                },
                {
                    id: ConfigurationLocalAccountingCustomersGridSidepanelComboElements.RATES,
                    entityKey: 'configuration.sections.rate'
                }
            ],
            obtainEntityList: async (entity: ConfigurationListSidePanelEntity): Promise<string[]> => {
                switch (entity.id as ConfigurationLocalAccountingCustomersGridSidepanelComboElements) {
                case ConfigurationLocalAccountingCustomersGridSidepanelComboElements.RATES:
                    return this._getAssignedRates(customerSelected)
                case ConfigurationLocalAccountingCustomersGridSidepanelComboElements.RULES:
                    return this._getAssignedRules(customerSelected)
                }
            }
        })
    }

    private async _getAssignedRates(customer: ConfigurationCustomerResponse): Promise<string[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'customer.id': GridUtils.getNumberFilterModel('equals', customer.id)
        }

        const laboratoryId = this.$store.getters.labId
        const response: StrictNullable<IResponseAgGrid<ConfigurationLocalAccountingBillableCustomerBillableRateResponse>> = await this.$_toCallSecure(
            configurationLocalAccountingBillableCustomerRatesService.paginator(
                filterModel,
                params,
                laboratoryId
            )
        )
        return response?.content.map((item: ConfigurationLocalAccountingBillableCustomerBillableRateResponse) => item.rate.name) ?? []
    }

    private async _getAssignedRules(customer: ConfigurationCustomerResponse): Promise<string[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'customer.id': {
                filterType: 'number',
                type: 'equals',
                filter: customer.id
            }
        }

        const laboratoryId = this.$store.getters.labId
        const response: StrictNullable<IResponseAgGrid<ConfigurationAutomaticCustomeRuleSetResponse>> = await this.$_toCallSecure(
            configurationLocalAccountingBillableCustomerRuleSetService.paginator(
                filterModel,
                params,
                laboratoryId
            )
        )
        return response?.content.map((item: ConfigurationAutomaticCustomeRuleSetResponse, index: number) => `${this.$tc('configuration.modal.client.rule')} ${index + 1}`) ?? []
    }

    private async _activateDeactivateSelection(active: boolean, accountingClients: ConfigurationCustomerResponse[]): Promise<void> {
        if (accountingClients?.length) {
            const batchRequests = accountingClients.filter(accountingClient => accountingClient.active !== active)
                .map(accountingUnit => ({
                    id: accountingUnit.id,
                    active
                }))
            if (await this._patchAccountingClients(batchRequests)) {
                this.reloadData()
            }
        }
    }

    private async _deletelocalAccountingBillableRates(requestDelete: EntityIdentifiersRequest): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    private async _patchAccountingClients(batchRequests: ConfigurationCustomerPatchInBatchRequest[]): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(this.state.laboratoryId, batchRequests))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///
    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }
}
